import { useLingui } from "@lingui/react/macro";
import {
	type PaymentAttempt,
	decidePaymentRecovery,
} from "@offstreetparking/portal-flow/components/CreatePermitWizard/components/PaymentForm";
import { useRef } from "react";
import { offstreetClient } from "~/services/offstreetApi";
import type { RouterOutputs } from "~/services/offstreetApi/types";
import { useAuthorizeNet } from "../../hooks/useAuthorizeNet/useAuthorizeNet";
type AuthorizeNetFormProps = {
	paymentSession: NonNullable<RouterOutputs["paymentSession"]["createPaymentSessionPublic"]["session"]>;
	formId: string;
	onError: (error?: {
		message: string;
		authorizeNetDeclineCode?: string;
	}) => void;
	onSuccess: (publicIdentifier: string) => void;
	setIsProcessing: (isProcessing: boolean) => void;
};
export const AuthorizeNetForm = ({
	paymentSession,
	formId,
	onSuccess,
	onError,
	setIsProcessing,
}: AuthorizeNetFormProps) => {
	const { t } = useLingui();
	const acceptUIButtonRef = useRef<HTMLButtonElement>(null);
	const { mutate: acknowledgeFailedPayment } = offstreetClient.paymentSession.acknowledgeFailedPayment.useMutation();
	const recover = (attempt: PaymentAttempt, publicIdentifier?: string) => {
		const { outcome, shouldAcknowledge } = decidePaymentRecovery(attempt);
		if (outcome === "succeeded") {
			onSuccess(publicIdentifier ?? "");
		} else {
			if (shouldAcknowledge) {
				acknowledgeFailedPayment({ paymentId: paymentSession.paymentId });
			}
			const declineCode = attempt.type === "completed" ? attempt.declineCode : undefined;
			onError({
				message:
					declineCode && attempt.type === "completed"
						? (attempt.reason ?? t`Payment error`)
						: t`Payment error`,
				authorizeNetDeclineCode: declineCode,
			});
		}
		setIsProcessing(false);
	};
	const { mutate: completePaymentSession } = offstreetClient.paymentSession.completePaymentSession.useMutation({
		onSuccess: (data) =>
			recover(
				{
					type: "completed",
					status: data.status,
					declineCode: data.status === "failed" ? data.declineCode : undefined,
					reason: data.status === "failed" ? data.reason : undefined,
				},
				data.status === "succeeded" ? data.publicIdentifier : undefined,
			),
		onError: () => recover({ type: "request-failed" }),
	});
	const { isReady, responseHandlerName } = useAuthorizeNet({
		paymentSession,
		callbacks: {
			onApproval: (nonce) => {
				setIsProcessing(true);
				completePaymentSession({
					paymentSessionId: paymentSession.paymentId,
					token: nonce,
				});
			},
			onError: () => recover({ type: "not-charged" }),
		},
	});
	const handleWizardSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!isReady || !acceptUIButtonRef.current) {
			return;
		}
		acceptUIButtonRef.current.click();
	};
	return (
		<>
			{/*
    The wizard's "Pay" button submits this form, which programmatically clicks the
    hidden AcceptUI button below. AcceptUI then opens its own lightbox to collect card data.
    */}
			<form id={formId} onSubmit={handleWizardSubmit} />

			{/*
    AcceptUI scans the DOM for elements with class="AcceptUI" on script load and wires
    up click handlers that open the lightbox. The button is hidden because the user clicks
    the wizard's submit button instead — we forward the click programmatically.
    */}
			<button
				ref={acceptUIButtonRef}
				type="button"
				className="AcceptUI"
				style={{
					display: "none",
				}}
				data-apiloginid={paymentSession.client?.apiLoginId ?? ""}
				data-clientkey={paymentSession.client?.clientKey ?? ""}
				data-billingaddressoptions='{"show":true,"required":true}'
				data-paymentoptions='{"showCreditCard":true,"showBankAccount":false}'
				data-acceptuiformbtntxt="Pay"
				data-acceptuiformheadertxt="Payment Information"
				data-responsehandler={responseHandlerName}
			>
				Pay with Authorize.net
			</button>
		</>
	);
};
